import { KeyboardArrowDown, Schedule } from '@mui/icons-material';
import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Typography,
  alpha,
} from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BulkActionButton } from '~/components/bulk-actions';
import type { DeviceBulkSelectionInput } from '~/generated/graphql';
import type { DeviceActionHandler } from './CommandPopover';

export type ScheduleBulkActionProps = {
  selected: DeviceBulkSelectionInput;
  totalCount: number;
  onScheduleShow: DeviceActionHandler;
  onComplete?: () => void;
};

export const ScheduleBulkAction = ({
  selected,
  totalCount,
  onScheduleShow,
  onComplete,
}: ScheduleBulkActionProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  const subject = t('pages.devices.bulk_actions.selection_subject_plural', {
    count: selected.ids?.length ?? totalCount,
  });

  return (
    <>
      <BulkActionButton
        ref={buttonRef}
        disableRipple
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={() => setOpen(true)}
      >
        {t('common.buttons.bulk_actions')}
        <KeyboardArrowDown />
      </BulkActionButton>
      <Popover
        anchorEl={buttonRef.current}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 320 } } }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
          }}
        >
          <Typography variant="body2" color="text.primary" fontWeight="bold" noWrap>
            {subject}
          </Typography>
        </Box>
        <MenuList>
          <MenuItem
            onClick={() => {
              onScheduleShow(selected, onComplete);
              close();
            }}
          >
            <ListItemIcon>
              <Schedule fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('pages.devices.bulk_actions.schedule_show')}</ListItemText>
          </MenuItem>
        </MenuList>
      </Popover>
    </>
  );
};
